import React, { useEffect, useState } from "react";

import { cn } from "../../../common/u1/cn";
import { FieldLabel } from "../../../common/u1/FieldLabel";
import { SelectField } from "../../../common/u1/SelectField";
import {
  days,
  dayMap,
  parseWorkingHours,
  formatWorkingHours,
} from "../../../common/WorkingHoursUI";

/* Working hours: two days and two times, stored as one string. */
export const WorkingHoursFields = ({
  value,
  onChange,
  error,
  className = "",
}) => {
  /* Seeded from the stored string, falling back to the live defaults. */
  const [form, setForm] = useState(() => {
    const parsed = value ? parseWorkingHours(value) : null;
    return parsed || { fromDay: "Mon", toDay: "Fri", openTime: "09:00", closeTime: "18:00" };
  });

  const set = (key, next) => setForm((prev) => ({ ...prev, [key]: next }));

  /* Transcribed: the parent holds the formatted string, and it is rewritten
     whenever any of the four parts changes. */
  useEffect(() => {
    if (form.openTime && form.closeTime) onChange?.(formatWorkingHours(form));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const dayOptions = days.map((d) => ({ value: d.value, label: dayMap[d.value] }));
  const timeField = cn(
    "h-11 w-full rounded-lg border bg-white px-3.5 text-[14px] text-[var(--ink)] font-sans",
    "transition-[border-color,box-shadow] duration-150",
    "focus:outline-none focus:border-[var(--orange)] focus:shadow-[0_0_0_4px_var(--focus-ring)]",
    error
      ? "border-[var(--coral)]"
      : "border-[var(--field-border)] hover:border-[var(--field-border-hover)]"
  );

  return (
    <fieldset className={cn("m-0 min-w-0 border-0 p-0", className)}>
      <legend className="mb-2.5 p-0 text-[13px] font-bold text-[var(--ink)] font-sans">
        Working hours
      </legend>

      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
        <SelectField
          label="From day"
          labelVariant="field"
          id="u1-from-day"
          value={form.fromDay}
          onChange={(e) => set("fromDay", e.target.value)}
          options={dayOptions}
        />
        <SelectField
          label="To day"
          labelVariant="field"
          id="u1-to-day"
          value={form.toDay}
          onChange={(e) => set("toDay", e.target.value)}
          options={dayOptions}
        />

        <div>
          <FieldLabel htmlFor="u1-opens" variant="field">
            Opens
          </FieldLabel>
          <input
            id="u1-opens"
            type="time"
            value={form.openTime}
            onChange={(e) => set("openTime", e.target.value)}
            className={timeField}
          />
        </div>

        <div>
          <FieldLabel htmlFor="u1-closes" variant="field">
            Closes
          </FieldLabel>
          <input
            id="u1-closes"
            type="time"
            value={form.closeTime}
            onChange={(e) => set("closeTime", e.target.value)}
            className={timeField}
          />
        </div>
      </div>

      {error && (
        <p role="alert" className="mt-1.5 text-[12px] text-[var(--coral-deep)] font-sans">
          {error}
        </p>
      )}
    </fieldset>
  );
};

export default WorkingHoursFields;
